import { z } from "zod";

export const JOB_STATUSES = [
  "queued",
  "extracting",
  "analysing",
  "completed",
  "failed",
] as const;
export const JOB_STATUS = {
  QUEUED: "queued",
  EXTRACTING: "extracting",
  ANALYSING: "analysing",
  COMPLETED: "completed",
  FAILED: "failed",
} as const satisfies Record<string, JobStatus>;
export const JobStatusSchema = z.enum(JOB_STATUSES);
export type JobStatus = z.infer<typeof JobStatusSchema>;

export const ISSUE_SEVERITIES = ["critical", "high", "medium", "low"] as const;
export const ISSUE_SEVERITY = {
  CRITICAL: "critical",
  HIGH: "high",
  MEDIUM: "medium",
  LOW: "low",
} as const satisfies Record<string, IssueSeverity>;
export const IssueSeveritySchema = z.enum(ISSUE_SEVERITIES);
export type IssueSeverity = z.infer<typeof IssueSeveritySchema>;

export const ISSUE_EFFORTS = ["S", "M", "L"] as const;
export const ISSUE_EFFORT = {
  S: "S",
  M: "M",
  L: "L",
} as const satisfies Record<string, IssueEffort>;
export const IssueEffortSchema = z.enum(ISSUE_EFFORTS);
export type IssueEffort = z.infer<typeof IssueEffortSchema>;

// `resolved` is the agent-owned closed state:
// set by yeet consolidation when a re-scan verifies the defect
// stopped reproducing, and flipped back to `open` by the same mechanism on
// recurrence. Analyst-set states (`done`, `ignored`) are never touched by
// that sweep. Append-only: issue_status is a pg enum.
export const ISSUE_STATUSES = [
  "open",
  "in_progress",
  "done",
  "ignored",
  "resolved",
] as const;
export const ISSUE_STATUS = {
  OPEN: "open",
  IN_PROGRESS: "in_progress",
  DONE: "done",
  IGNORED: "ignored",
  RESOLVED: "resolved",
} as const satisfies Record<string, IssueStatus>;
export const IssueStatusSchema = z.enum(ISSUE_STATUSES);
export type IssueStatus = z.infer<typeof IssueStatusSchema>;

export const OPEN_ISSUE_STATUSES = [
  "open",
  "in_progress",
] as const satisfies readonly IssueStatus[];
export const OpenIssueStatusSchema = z.enum(OPEN_ISSUE_STATUSES);
export type OpenIssueStatus = z.infer<typeof OpenIssueStatusSchema>;

// What the latest scan observed about an issue, derived at read time from the
// yeet run record (workers.yeet_finding / yeet_check_judgment /
// yeet_subcriterion_assessment and pages.content_text) — a SEPARATE axis
// from the analyst/agent `status` above. Never stored: no pgEnum, no column.
// The derivation (backend issue-lifecycle-state.ts) is the single source of the
// rules. `legacy` = non-yeet fingerprint the agent never re-checks;
// `not_reverified` = existed before but its page was not fully re-checked this
// scan (distinct from still broken).
export const ISSUE_LIFECYCLE_STATES = [
  "new",
  "reopened",
  "still_open",
  "verified_fixed",
  "not_reverified",
  "legacy",
] as const;
export const ISSUE_LIFECYCLE_STATE = {
  NEW: "new",
  REOPENED: "reopened",
  STILL_OPEN: "still_open",
  VERIFIED_FIXED: "verified_fixed",
  NOT_REVERIFIED: "not_reverified",
  LEGACY: "legacy",
} as const satisfies Record<string, IssueLifecycleState>;
export const IssueLifecycleStateSchema = z.enum(ISSUE_LIFECYCLE_STATES);
export type IssueLifecycleState = z.infer<typeof IssueLifecycleStateSchema>;

// `repairing` is a completed/failed run reopened to re-analyse selected pages
// against their existing captures. It behaves like `running` for dispatch,
// canonicalization and completion; the run keeps its identity and gains a new
// job generation when it lands.
export const CRAWL_RUN_STATUSES = [
  "running",
  "repairing",
  "completed",
  "failed",
] as const;
export const CRAWL_RUN_STATUS = {
  RUNNING: "running",
  REPAIRING: "repairing",
  COMPLETED: "completed",
  FAILED: "failed",
} as const satisfies Record<string, CrawlRunStatus>;
export const CrawlRunStatusSchema = z.enum(CRAWL_RUN_STATUSES);
export type CrawlRunStatus = z.infer<typeof CrawlRunStatusSchema>;

export const CRAWL_FRONTIER_STATUSES = [
  "pending",
  "in_flight",
  "scraped",
  "failed",
  "skipped",
] as const;
export const CRAWL_FRONTIER_STATUS = {
  PENDING: "pending",
  IN_FLIGHT: "in_flight",
  SCRAPED: "scraped",
  FAILED: "failed",
  SKIPPED: "skipped",
} as const satisfies Record<string, CrawlFrontierStatus>;
export const CrawlFrontierStatusSchema = z.enum(CRAWL_FRONTIER_STATUSES);
export type CrawlFrontierStatus = z.infer<typeof CrawlFrontierStatusSchema>;

export const CRAWL_DISCOVERED_VIAS = ["seed", "link"] as const;
export const CRAWL_DISCOVERED_VIA = {
  SEED: "seed",
  LINK: "link",
} as const satisfies Record<string, CrawlDiscoveredVia>;
export const CrawlDiscoveredViaSchema = z.enum(CRAWL_DISCOVERED_VIAS);
export type CrawlDiscoveredVia = z.infer<typeof CrawlDiscoveredViaSchema>;

export const CRAWL_FAILURE_REASONS = [
  "fetch_failed",
  "extract_failed",
  "attempts_exceeded",
  // Provider/capture failure modes for the scrape-capture pipeline.
  // `extract_failed` above is kept only for the legacy local-extraction path;
  // do not reuse it for provider-capture failures.
  "provider_failed",
  "capture_contract_failed",
  "artifact_store_failed",
  // Yeet path: the sandbox attempt failed (bad exit, timeout, or exit without
  // a complete_page call). Reported by the dispatcher, never retried into the
  // classic pipeline.
  "yeet_attempt_failed",
  // The run was aborted by an operator while this page was still outstanding
  // (run abort). Distinct from attempts_exceeded: the page itself was
  // never given up on.
  "aborted",
] as const;
export const CRAWL_FAILURE_REASON = {
  FETCH_FAILED: "fetch_failed",
  EXTRACT_FAILED: "extract_failed",
  ATTEMPTS_EXCEEDED: "attempts_exceeded",
  PROVIDER_FAILED: "provider_failed",
  CAPTURE_CONTRACT_FAILED: "capture_contract_failed",
  ARTIFACT_STORE_FAILED: "artifact_store_failed",
  YEET_ATTEMPT_FAILED: "yeet_attempt_failed",
  ABORTED: "aborted",
} as const satisfies Record<string, CrawlFailureReason>;
export const CrawlFailureReasonSchema = z.enum(CRAWL_FAILURE_REASONS);
export type CrawlFailureReason = z.infer<typeof CrawlFailureReasonSchema>;

export const HEALTH_STATES = ["good", "watch", "poor", "nodata"] as const;
export const HEALTH_STATE = {
  GOOD: "good",
  WATCH: "watch",
  POOR: "poor",
  NODATA: "nodata",
} as const satisfies Record<string, HealthState>;
export const HealthStateSchema = z.enum(HEALTH_STATES);
export type HealthState = z.infer<typeof HealthStateSchema>;

// A run still accepting page work: mid-crawl, or reopened by a repair.
// Completion treats both identically — drain the frontier, then materialize a
// generation.
export const ACTIVE_CRAWL_RUN_STATUSES = [
  "running",
  "repairing",
] as const satisfies readonly CrawlRunStatus[];
export type ActiveCrawlRunStatus = (typeof ACTIVE_CRAWL_RUN_STATUSES)[number];

export function isActiveCrawlRunStatus(
  status: CrawlRunStatus | undefined,
): status is ActiveCrawlRunStatus {
  return ACTIVE_CRAWL_RUN_STATUSES.some((active) => active === status);
}

// What a run-level canonicalization pass did. `noop` means there was nothing
// to canonicalize, which is distinct from `deferred` (work still outstanding).
export const CANONICALIZATION_OUTCOMES = [
  "applied",
  "deferred",
  "canonicalizing",
  "noop",
] as const;
export const CanonicalizationOutcomeSchema = z.enum(CANONICALIZATION_OUTCOMES);
export type CanonicalizationOutcome = z.infer<
  typeof CanonicalizationOutcomeSchema
>;
export const CANONICALIZATION_OUTCOME = {
  APPLIED: "applied",
  DEFERRED: "deferred",
  CANONICALIZING: "canonicalizing",
  NOOP: "noop",
} as const satisfies Record<string, CanonicalizationOutcome>;

// A job that has not reached a terminal state: work is still in flight, so a
// surface shows progress rather than a score or a failure.
export const IN_FLIGHT_JOB_STATUSES: readonly JobStatus[] = [
  "queued",
  "extracting",
  "analysing",
];
